import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getNotifications, deleteNotification } from '../../actions/notification';
import { 
  List, 
  Plus, 
  Trash2, 
  Edit, 
  Eye, 
  Search,
  Bell,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  User
} from 'lucide-react';

const Notifications = ({ 
  getNotifications, 
  deleteNotification, 
  notification: { notifications, loading }
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    getNotifications();
  }, [getNotifications]);

  useEffect(() => {
    if (notifications) {
      setFilteredNotifications(
        notifications.filter(item => 
          item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.notificationType?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [notifications, searchTerm]);

  const handleSearch = e => {
    setSearchTerm(e.target.value);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (itemToDelete) {
      deleteNotification(itemToDelete._id);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const getNotificationTypeIcon = (notificationType) => {
    switch (notificationType) {
      case 'stock':
        return <i className="fas fa-boxes me-2 text-warning"></i>;
      case 'vm-status':
        return <i className="fas fa-server me-2 text-info"></i>;
      case 'cans':
        return <i className="fas fa-wine-bottle me-2 text-success"></i>;
      case 'payment':
        return <i className="fas fa-credit-card me-2 text-primary"></i>;
      case 'order':
        return <i className="fas fa-shopping-cart me-2 text-purple"></i>;
      case 'update':
        return <i className="fas fa-sync-alt me-2 text-secondary"></i>;
      case 'offers':
        return <i className="fas fa-gift me-2 text-danger"></i>;
      case 'lock-status':
        return <i className="fas fa-lock me-2 text-dark"></i>;
      case 'delivered':
        return <i className="fas fa-check-circle me-2 text-success"></i>;
      case 'delivery':
        return <i className="fas fa-truck me-2 text-info"></i>;
      default:
        return <i className="fas fa-bell me-2 text-secondary"></i>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusBadge = (item) => {
    if (item.expired) {
      return <span className="badge bg-danger">Expired</span>;
    } else if (item.read) {
      return <span className="badge bg-success">Read</span>;
    } else if (item.notified) {
      return <span className="badge bg-warning">Notified</span>;
    } else {
      return <span className="badge bg-secondary">New</span>;
    }
  };

  return (
    <Fragment>
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-0 text-gray-800 d-flex align-items-center">
              <Bell size={28} className="me-3 text-primary" />
              Notifications
            </h1>
            <p className="text-muted">
              Manage system notifications and alerts
            </p>
          </div>
          <Link to="/inventory/v1.2/notifications/new" className="btn btn-primary d-flex align-items-center">
            <Plus size={18} className="me-2" />
            Add New Notification
          </Link>
        </div>

        {/* Search Bar */}
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <div className="input-group">
              <span className="input-group-text bg-light">
                <Search size={18} className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Search by title, description, or type..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="row">
            {filteredNotifications.length > 0 ? (
              filteredNotifications.map(item => (
                <div key={item._id} className="col-lg-6 col-xl-4 mb-4">
                  <div className="card shadow-sm h-100">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 d-flex align-items-center">
                        {getNotificationTypeIcon(item.notificationType)}
                        {item.title}
                      </h5>
                      {getStatusBadge(item)}
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <p className="text-muted mb-1">Type:</p>
                        <p className="fw-bold mb-0">{item.notificationType}</p>
                      </div>
                      <div className="mb-3">
                        <p className="text-muted mb-1">Description:</p>
                        <p className="text-truncate mb-0" style={{ maxWidth: '250px' }}>
                          {item.description}
                        </p>
                      </div>
                      <div className="mb-3">
                        <p className="text-muted mb-1">User:</p>
                        <p className="fw-bold mb-0">
                          {item.user ? (
                            <span className="d-flex align-items-center">
                              <User size={14} className="me-1" />
                              {item.user}
                            </span>
                          ) : (
                            <span className="text-muted">All Users</span>
                          )}
                        </p>
                      </div>
                      <div className="mb-3">
                        <p className="text-muted mb-1">Expire Date:</p>
                        <p className="fw-bold mb-0 d-flex align-items-center">
                          <Clock size={14} className="me-1" />
                          {formatDate(item.expireDate)}
                        </p>
                      </div>
                      <div className="d-flex align-items-center mb-3">
                        <span className="me-2">Status:</span>
                        {item.expired ? (
                          <span className="d-flex align-items-center text-danger">
                            <XCircle size={16} className="me-1" />
                            Expired
                          </span>
                        ) : item.read ? (
                          <span className="d-flex align-items-center text-success">
                            <CheckCircle size={16} className="me-1" />
                            Read
                          </span>
                        ) : item.notified ? (
                          <span className="d-flex align-items-center text-warning">
                            <Bell size={16} className="me-1" />
                            Notified
                          </span>
                        ) : (
                          <span className="d-flex align-items-center text-secondary">
                            <AlertTriangle size={16} className="me-1" />
                            New
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="card-footer bg-white d-flex justify-content-between">
                      <Link
                        to={`/inventory/v1.2/notifications/${item._id}`}
                        className="btn btn-sm btn-outline-primary d-flex align-items-center"
                      >
                        <Eye size={14} className="me-1" />
                        View
                      </Link>
                      <button
                        onClick={() => handleDeleteClick(item)}
                        className="btn btn-sm btn-outline-danger d-flex align-items-center"
                      >
                        <Trash2 size={14} className="me-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12">
                <div className="alert alert-info">
                  No notifications found. {searchTerm && 'Try adjusting your search.'}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-0">
                  <h5 className="modal-title d-flex align-items-center text-danger">
                    <AlertTriangle size={24} className="me-2" />
                    Confirm Delete
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={cancelDelete}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <div className="mb-3">
                      <Trash2 size={48} className="text-danger" />
                    </div>
                    <h6 className="mb-3">Delete "{itemToDelete?.title}"?</h6>
                    <p className="text-muted mb-4">
                      This action cannot be undone. The notification will be permanently deleted.
                    </p>
                    <div className="alert alert-warning d-flex align-items-center">
                      <AlertTriangle size={20} className="me-2" />
                      <small>
                        <strong>Warning:</strong> This will permanently delete this notification.
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={cancelDelete}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={confirmDelete}
                  >
                    <Trash2 size={16} className="me-2" />
                    Delete Notification
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
};

Notifications.propTypes = {
  getNotifications: PropTypes.func.isRequired,
  deleteNotification: PropTypes.func.isRequired,
  notification: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  notification: state.notification
});

export default connect(
  mapStateToProps,
  { getNotifications, deleteNotification }
)(Notifications);
